import { z } from 'zod';

import type {
  CreateShipmentWeighCaptureLinkRequest,
  CreateShipmentWeighCaptureLinkResponse,
  ListShipmentWeighCaptureLinkTargetsRequest,
  ListShipmentWeighCaptureLinkTargetsResponse,
  ListShipmentWeighCaptureLinksRequest,
  ListShipmentWeighCaptureLinksResponse,
  PublicShipmentWeighCaptureContextDto,
  RecoverShipmentWeighCaptureLinkRequest,
  RecoverShipmentWeighCaptureLinkResponse,
  ResolveShipmentWeighCaptureLinkRequest,
  ResolveShipmentWeighCaptureLinkResponse,
  RevokeShipmentWeighCaptureLinkRequest,
  RevokeShipmentWeighCaptureLinkResponse,
  ShipmentWeighCaptureLinkDto,
  SubmitShipmentWeighCaptureLinkRequest,
  SubmitShipmentWeighCaptureLinkResponse,
} from './shipment-weigh-capture-link.dto.js';
import {
  IsoDateTimeSchema,
  SecureShipmentWeighScaleRoleSchema,
  ShipmentQuickCaptureCannotSubmitReasonSchema,
  ShipmentQuickCaptureCapturePhaseSchema,
  ShipmentQuickCaptureMaterialTaskStatusSchema,
  ShipmentQuickCaptureTargetKindSchema,
  ShipmentWeighTypeSchema,
} from './shipments-types.js';
import { ShipmentMaterialRefSchema } from './shipment-weigh-evidence.schema.js';
import {
  DriverShipmentWeighComparisonSummarySchema,
  DriverShipmentWeighEventSummarySchema,
  DriverShipmentWeighEvidenceSummarySchema,
  DriverShipmentWeighNetFeedbackSchema,
  ShipmentWeighEventRecoveryActionSchema,
  ShipmentQuickCaptureTargetSchema,
  ShipmentWeighOperationalCardSchema,
} from './shipment-weigh-evidence.schema.js';
import { OutboundRecordTypeSchema } from '../outbound/outbound-record-reference.schema.js';

const trimmedStringSchema = z.string().trim().min(1);
const noteSchema = trimmedStringSchema.nullable().optional();
const secretSchema = z.string().trim().min(1);
const optionalNullablePositiveNumberSchema = z.number().gt(0).nullable().optional();
const nullableNonNegativeNumberSchema = z.number().min(0).nullable();
const secureLinkOutboundReferenceSchema = {
  shipmentId: z.uuid().optional(),
  outboundRecordType: OutboundRecordTypeSchema.optional(),
  outboundRecordId: z.uuid().optional(),
};

export const ShipmentWeighCaptureLinkStatusSchema = z.enum([
  'ACTIVE',
  'EXPIRED',
  'REVOKED',
  'EXHAUSTED',
]);

export const ShipmentWeighCaptureLinkUnavailableReasonSchema = z.enum([
  'LINK_EXPIRED',
  'LINK_REVOKED',
  'SUBMISSION_LIMIT_REACHED',
  'TARGET_UNAVAILABLE',
]);

export const PublicShipmentWeighCaptureLinkStatusSchema = z.enum([
  'AVAILABLE',
  'UNAVAILABLE',
  'SUBMISSION_LIMIT_REACHED',
]);

export const SecureShipmentWeighScaleRolesSchema = z
  .array(SecureShipmentWeighScaleRoleSchema)
  .nonempty()
  .transform((roles) => Array.from(new Set(roles)));

export const ShipmentWeighCaptureLinkSchema: z.ZodType<ShipmentWeighCaptureLinkDto> = z
  .object({
    linkId: z.uuid(),
    companyId: z.uuid(),
    shipmentId: z.uuid().nullable(),
    outboundRecordType: OutboundRecordTypeSchema,
    outboundRecordId: z.uuid(),
    shipmentMaterialRef: ShipmentMaterialRefSchema.nullable(),
    allowedScaleRoles: z.array(SecureShipmentWeighScaleRoleSchema).nonempty(),
    maxSubmissions: z.number().int().min(1).nullable(),
    usedCount: z.number().int().min(0),
    expiresAt: IsoDateTimeSchema,
    recipientLabel: trimmedStringSchema.nullable(),
    purposeNote: trimmedStringSchema.nullable(),
    createdByUserId: z.uuid().nullable(),
    createdAt: IsoDateTimeSchema,
    revokedAt: IsoDateTimeSchema.nullable(),
    revokedByUserId: z.uuid().nullable(),
    revokeReason: trimmedStringSchema.nullable(),
    status: ShipmentWeighCaptureLinkStatusSchema,
    captureAvailable: z.boolean(),
    captureUnavailableReason: ShipmentWeighCaptureLinkUnavailableReasonSchema.nullable(),
  })
  .strict();

export const CreateShipmentWeighCaptureLinkRequestSchema: z.ZodType<CreateShipmentWeighCaptureLinkRequest> =
  z
    .object({
      companyId: z.uuid(),
      ...secureLinkOutboundReferenceSchema,
      shipmentMaterialRef: ShipmentMaterialRefSchema.optional(),
      allowedScaleRoles: SecureShipmentWeighScaleRolesSchema.optional(),
      maxSubmissions: z.number().int().min(1).nullable().optional(),
      expiresAt: IsoDateTimeSchema.optional(),
      recipientLabel: noteSchema,
      purposeNote: noteSchema,
    })
    .strict()
    .superRefine(validateSecureLinkOutboundReference);

export const CreateShipmentWeighCaptureLinkResponseSchema: z.ZodType<CreateShipmentWeighCaptureLinkResponse> =
  z
    .object({
      link: ShipmentWeighCaptureLinkSchema,
      captureUrl: trimmedStringSchema,
    })
    .strict();

export const ListShipmentWeighCaptureLinksRequestSchema: z.ZodType<ListShipmentWeighCaptureLinksRequest> =
  z
    .object({
      companyId: z.uuid(),
      ...secureLinkOutboundReferenceSchema,
    })
    .strict()
    .superRefine(validateSecureLinkOutboundReference);

export const ListShipmentWeighCaptureLinksResponseSchema: z.ZodType<ListShipmentWeighCaptureLinksResponse> =
  z
    .object({
      links: z.array(ShipmentWeighCaptureLinkSchema),
    })
    .strict();

export const ListShipmentWeighCaptureLinkTargetsRequestSchema: z.ZodType<ListShipmentWeighCaptureLinkTargetsRequest> =
  z
    .object({
      companyId: z.uuid(),
    })
    .strict();

export const ListShipmentWeighCaptureLinkTargetsResponseSchema: z.ZodType<ListShipmentWeighCaptureLinkTargetsResponse> =
  z
    .object({
      targets: z.array(ShipmentQuickCaptureTargetSchema),
    })
    .strict();

export const RevokeShipmentWeighCaptureLinkRequestSchema: z.ZodType<RevokeShipmentWeighCaptureLinkRequest> =
  z
    .object({
      companyId: z.uuid(),
      ...secureLinkOutboundReferenceSchema,
      linkId: z.uuid(),
      reason: trimmedStringSchema,
    })
    .strict()
    .superRefine(validateSecureLinkOutboundReference);

export const RevokeShipmentWeighCaptureLinkResponseSchema: z.ZodType<RevokeShipmentWeighCaptureLinkResponse> =
  z
    .object({
      link: ShipmentWeighCaptureLinkSchema,
    })
    .strict();

export const PublicShipmentWeighCaptureContextSchema: z.ZodType<PublicShipmentWeighCaptureContextDto> =
  z
    .object({
      linkId: z.uuid(),
      targetKind: ShipmentQuickCaptureTargetKindSchema,
      capturePhase: ShipmentQuickCaptureCapturePhaseSchema,
      shipmentId: z.uuid().nullable(),
      outboundRecordType: OutboundRecordTypeSchema,
      outboundRecordLabel: trimmedStringSchema,
      shipmentReference: trimmedStringSchema,
      shipmentMaterialRef: ShipmentMaterialRefSchema.nullable(),
      materialLabel: trimmedStringSchema.nullable(),
      receiverLabel: trimmedStringSchema.nullable(),
      huExpectedPhysicalWeightLb: nullableNonNegativeNumberSchema,
      reportableWeightLb: nullableNonNegativeNumberSchema,
      evidenceSummary: DriverShipmentWeighEvidenceSummarySchema,
      comparison: DriverShipmentWeighComparisonSummarySchema,
      netFeedback: DriverShipmentWeighNetFeedbackSchema,
      weighCards: z.array(ShipmentWeighOperationalCardSchema),
      recentEvents: z.array(DriverShipmentWeighEventSummarySchema),
      materialTaskStatus: ShipmentQuickCaptureMaterialTaskStatusSchema,
      materialTaskStatusLabel: trimmedStringSchema,
      canSubmit: z.boolean(),
      cannotSubmitReason: ShipmentQuickCaptureCannotSubmitReasonSchema.nullable(),
      allowedScaleRoles: z.array(SecureShipmentWeighScaleRoleSchema).nonempty(),
      maxSubmissions: z.number().int().min(1).nullable(),
      usedCount: z.number().int().min(0),
      expiresAt: IsoDateTimeSchema,
    })
    .strict();

export const ResolveShipmentWeighCaptureLinkRequestSchema: z.ZodType<ResolveShipmentWeighCaptureLinkRequest> =
  z
    .object({
      linkId: z.uuid(),
      secret: secretSchema,
    })
    .strict();

export const ResolveShipmentWeighCaptureLinkResponseSchema: z.ZodType<ResolveShipmentWeighCaptureLinkResponse> =
  z
    .object({
      status: PublicShipmentWeighCaptureLinkStatusSchema,
      capture: PublicShipmentWeighCaptureContextSchema.nullable(),
    })
    .strict();

export const SubmitShipmentWeighCaptureLinkRequestSchema: z.ZodType<SubmitShipmentWeighCaptureLinkRequest> =
  z
    .object({
      linkId: z.uuid(),
      secret: secretSchema,
      continuationOfEventId: z.uuid().nullable().optional(),
      scaleRole: SecureShipmentWeighScaleRoleSchema,
      scaleLabel: noteSchema,
      weighType: ShipmentWeighTypeSchema,
      weightLb: optionalNullablePositiveNumberSchema,
      grossWeightLb: optionalNullablePositiveNumberSchema,
      tareWeightLb: optionalNullablePositiveNumberSchema,
      photoUrl: noteSchema,
      notes: noteSchema,
      driverName: noteSchema,
      driverCompany: noteSchema,
      driverPhone: noteSchema,
    })
    .strict()
    .superRefine((value, ctx) => {
      if (value.scaleRole === 'OTHER' && !value.scaleLabel?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'scaleLabel is required for OTHER scale role.',
          path: ['scaleLabel'],
        });
      }

      if (value.weighType === 'MANUAL_PAIR') {
        if (typeof value.grossWeightLb !== 'number') {
          ctx.addIssue({
            code: 'custom',
            message: 'grossWeightLb is required for MANUAL_PAIR.',
            path: ['grossWeightLb'],
          });
        }
        if (typeof value.tareWeightLb !== 'number') {
          ctx.addIssue({
            code: 'custom',
            message: 'tareWeightLb is required for MANUAL_PAIR.',
            path: ['tareWeightLb'],
          });
        }
        if (
          typeof value.grossWeightLb === 'number' &&
          typeof value.tareWeightLb === 'number' &&
          value.grossWeightLb < value.tareWeightLb
        ) {
          ctx.addIssue({
            code: 'custom',
            message: 'grossWeightLb must be greater than or equal to tareWeightLb.',
            path: ['grossWeightLb'],
          });
        }
        return;
      }

      if (typeof value.weightLb !== 'number') {
        ctx.addIssue({
          code: 'custom',
          message: 'weightLb is required for GROSS, TARE, and NET_DIRECT.',
          path: ['weightLb'],
        });
      }
    });

export const SubmitShipmentWeighCaptureLinkResponseSchema: z.ZodType<SubmitShipmentWeighCaptureLinkResponse> =
  z
    .object({
      status: z.literal('SUBMITTED'),
      weighEventId: z.uuid(),
      submittedAt: IsoDateTimeSchema,
      capture: PublicShipmentWeighCaptureContextSchema,
      event: DriverShipmentWeighEventSummarySchema,
    })
    .strict();

export const RecoverShipmentWeighCaptureLinkRequestSchema: z.ZodType<RecoverShipmentWeighCaptureLinkRequest> =
  z
    .object({
      linkId: z.uuid(),
      secret: secretSchema,
      recoveryToken: secretSchema,
      weighEventId: z.uuid(),
      action: ShipmentWeighEventRecoveryActionSchema,
      scaleRole: SecureShipmentWeighScaleRoleSchema.nullable().optional(),
      scaleLabel: noteSchema,
      weighType: ShipmentWeighTypeSchema.nullable().optional(),
      weightLb: optionalNullablePositiveNumberSchema,
      grossWeightLb: optionalNullablePositiveNumberSchema,
      tareWeightLb: optionalNullablePositiveNumberSchema,
      photoUrl: noteSchema,
      notes: noteSchema,
    })
    .strict()
    .superRefine(validatePublicRecoveryReplacementValues);

export const RecoverShipmentWeighCaptureLinkResponseSchema: z.ZodType<RecoverShipmentWeighCaptureLinkResponse> =
  z
    .object({
      status: z.literal('RECOVERED'),
      action: ShipmentWeighEventRecoveryActionSchema,
      weighEventId: z.uuid(),
      recoveredAt: IsoDateTimeSchema,
      capture: PublicShipmentWeighCaptureContextSchema,
      event: DriverShipmentWeighEventSummarySchema,
    })
    .strict();

function validatePublicRecoveryReplacementValues(
  value: Pick<
    RecoverShipmentWeighCaptureLinkRequest,
    'action' | 'scaleRole' | 'weighType' | 'weightLb' | 'grossWeightLb' | 'tareWeightLb'
  >,
  ctx: z.RefinementCtx,
): void {
  if (value.action === 'WITHDRAW') {
    return;
  }

  if (!value.scaleRole) {
    ctx.addIssue({
      code: 'custom',
      message: 'scaleRole is required when replacing a shipment weigh event.',
      path: ['scaleRole'],
    });
  }
  if (!value.weighType) {
    ctx.addIssue({
      code: 'custom',
      message: 'weighType is required when replacing a shipment weigh event.',
      path: ['weighType'],
    });
    return;
  }

  validateSubmittedWeighValues(value, ctx);
}

function validateSecureLinkOutboundReference(value: unknown, ctx: z.RefinementCtx): void {
  const reference = value as {
    shipmentId?: string;
    outboundRecordType?: string;
    outboundRecordId?: string;
  };
  const hasShipmentId = reference.shipmentId !== undefined;
  const hasType = reference.outboundRecordType !== undefined;
  const hasId = reference.outboundRecordId !== undefined;

  if (hasType !== hasId) {
    ctx.addIssue({
      code: 'custom',
      message: 'outboundRecordType and outboundRecordId must be provided together.',
      path: [hasType ? 'outboundRecordId' : 'outboundRecordType'],
    });
    return;
  }

  if (!hasShipmentId && !hasType) {
    ctx.addIssue({
      code: 'custom',
      message: 'Either shipmentId or outboundRecordType and outboundRecordId must be provided.',
      path: ['shipmentId'],
    });
    return;
  }

  if (hasShipmentId && hasType && reference.outboundRecordType !== 'SHIPMENT') {
    ctx.addIssue({
      code: 'custom',
      message: 'shipmentId can only be combined with SHIPMENT outbound records.',
      path: ['shipmentId'],
    });
    return;
  }

  if (
    hasShipmentId &&
    reference.outboundRecordType === 'SHIPMENT' &&
    reference.outboundRecordId &&
    reference.shipmentId !== reference.outboundRecordId
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'shipmentId must match outboundRecordId for SHIPMENT outbound records.',
      path: ['outboundRecordId'],
    });
  }
}

function validateSubmittedWeighValues(
  value: Pick<
    RecoverShipmentWeighCaptureLinkRequest,
    'weighType' | 'weightLb' | 'grossWeightLb' | 'tareWeightLb'
  >,
  ctx: z.RefinementCtx,
): void {
  if (value.weighType === 'MANUAL_PAIR') {
    if (typeof value.grossWeightLb !== 'number') {
      ctx.addIssue({
        code: 'custom',
        message: 'grossWeightLb is required for MANUAL_PAIR.',
        path: ['grossWeightLb'],
      });
    }
    if (typeof value.tareWeightLb !== 'number') {
      ctx.addIssue({
        code: 'custom',
        message: 'tareWeightLb is required for MANUAL_PAIR.',
        path: ['tareWeightLb'],
      });
    }
    if (
      typeof value.grossWeightLb === 'number' &&
      typeof value.tareWeightLb === 'number' &&
      value.grossWeightLb < value.tareWeightLb
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'grossWeightLb must be greater than or equal to tareWeightLb.',
        path: ['grossWeightLb'],
      });
    }
    return;
  }

  if (typeof value.weightLb !== 'number') {
    ctx.addIssue({
      code: 'custom',
      message: 'weightLb is required for GROSS, TARE, and NET_DIRECT.',
      path: ['weightLb'],
    });
  }
}
